/**
 * MapExperience — V3 Day 3.1 (2026-05-09)
 *
 * Full-screen interactive map modal that takes the same Leaflet stack
 * as the inline KhoudMap but layers on the v1 demand-narrative UX:
 *
 *   - 11 category filter chips (multi-select, "All" toggles everything)
 *   - 3 demand-theme cards (Education Belt / Tech Hub / Medical Corridor)
 *     with verbatim Arabic copy from almoftah.online v1
 *   - Drive-time rings (5/10/15 min) toggleable on the KEY2 anchor
 *   - POI detail panel with Haversine distance + "Open in Google Maps"
 *   - Insight statement at the bottom (verbatim Arabic from v1)
 *
 * Layout — Desktop: split (map 65% / sidebar 35%); Mobile: tabs stack.
 *
 * Data:
 *   pois.json (untouched; canonical 16 POIs with lat/lng)
 *   demand-themes.json (NEW; theme→POI grouping by name_en)
 *
 * No new deps. No KhoudMap.tsx edits. No pois.json edits.
 */

import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import {
  X,
  GraduationCap,
  Cpu,
  HeartPulse,
  ShoppingBag,
  Plane,
  Wrench,
  Star,
  Factory,
  Route,
  Building2,
  Filter,
  Map as MapIcon,
  Sparkles,
  Compass,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  type LucideIcon,
} from 'lucide-react';
import poisData from '@data/pois.json';
import demandData from '@data/demand-themes.json';
import { useT, type StringKey } from '@lib/i18n';
import { useSessionStore } from '@stores/sessionStore';
import type { POI, DemandTheme } from '@lib/types';

interface MapExperienceProps {
  open: boolean;
  onClose: () => void;
}

type CategoryKey =
  | 'education'
  | 'healthcare'
  | 'tech'
  | 'shopping'
  | 'transport'
  | 'services'
  | 'landmark'
  | 'industry'
  | 'road'
  | 'neighborhood';

const CATEGORY_KEYS: CategoryKey[] = [
  'education',
  'healthcare',
  'tech',
  'shopping',
  'transport',
  'services',
  'landmark',
  'industry',
  'road',
  'neighborhood',
];

const CATEGORY_META: Record<
  CategoryKey,
  { icon: LucideIcon; color: string; bgClass: string }
> = {
  education:    { icon: GraduationCap, color: '#1e40af', bgClass: 'bg-blue-500'   },
  healthcare:   { icon: HeartPulse,    color: '#dc2626', bgClass: 'bg-red-500'    },
  tech:         { icon: Cpu,           color: '#0891b2', bgClass: 'bg-cyan-500'   },
  shopping:     { icon: ShoppingBag,   color: '#7c3aed', bgClass: 'bg-violet-500' },
  transport:    { icon: Plane,         color: '#0284c7', bgClass: 'bg-sky-500'    },
  services:     { icon: Wrench,        color: '#475569', bgClass: 'bg-slate-500'  },
  landmark:     { icon: Star,          color: '#d97706', bgClass: 'bg-amber-500'  },
  industry:     { icon: Factory,       color: '#ea580c', bgClass: 'bg-orange-600' },
  road:         { icon: Route,         color: '#6b7280', bgClass: 'bg-gray-500'   },
  neighborhood: { icon: Building2,     color: '#0d9488', bgClass: 'bg-teal-500'   },
};

const THEME_ICON: Record<DemandTheme['id'], LucideIcon> = {
  edu: GraduationCap,
  tech: Cpu,
  med: HeartPulse,
};

// ─── Geo helpers ────────────────────────────────────────────────
function haversineKm(a: [number, number], b: [number, number]): number {
  const R = 6371;
  const dLat = ((b[0] - a[0]) * Math.PI) / 180;
  const dLng = ((b[1] - a[1]) * Math.PI) / 180;
  const lat1 = (a[0] * Math.PI) / 180;
  const lat2 = (b[0] * Math.PI) / 180;
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
}

// 30 km/h average urban speed → 0.5 km/min
function approxDriveMin(km: number): number {
  return Math.max(1, Math.round(km / 0.5));
}

// ─── Marker icon factory ────────────────────────────────────────
function buildMarkerIcon(opts: {
  color: string;
  size: number;
  ring?: boolean;
  pulse?: boolean;
}): L.DivIcon {
  const { color, size, ring = false, pulse = false } = opts;
  const ringStyle = ring
    ? `box-shadow:0 0 0 4px rgba(212,175,55,.55), 0 1px 6px rgba(0,0,0,.4);`
    : `box-shadow:0 1px 4px rgba(0,0,0,.4);`;
  const pulseClass = pulse ? 'me-pulse' : '';
  return L.divIcon({
    className: `me-marker ${pulseClass}`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    html: `<span style="display:inline-block;width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2px solid #fff;${ringStyle}"></span>`,
  });
}

// ─── Component ──────────────────────────────────────────────────
export default function MapExperience({ open, onClose }: MapExperienceProps) {
  const t = useT();
  const lang = useSessionStore((s) => s.language);
  const isAr = lang === 'ar';
  const dir: 'ltr' | 'rtl' = isAr ? 'rtl' : 'ltr';

  const allPois = (poisData.pois as POI[]) ?? [];
  const center = (poisData.center as [number, number]) ?? [23.5726, 58.1763];
  const themes = (demandData.themes as DemandTheme[]) ?? [];

  // Resolve KEY2 anchor (project category)
  const key2Poi = useMemo(
    () => allPois.find((p) => p.category === 'project') ?? null,
    [allPois]
  );

  // Project theme membership onto each POI
  const poisWithTheme: POI[] = useMemo(
    () =>
      allPois.map((p) => {
        const t = themes.find((th) => th.poiNamesEn.includes(p.name_en));
        return t ? { ...p, themeId: t.id } : p;
      }),
    [allPois, themes]
  );

  // ─── State ────────────────────────────────────────────────────
  const [activeCats, setActiveCats] = useState<Set<CategoryKey>>(
    () => new Set(CATEGORY_KEYS)
  );
  const [activeThemeId, setActiveThemeId] = useState<DemandTheme['id'] | null>(null);
  const [selectedPoi, setSelectedPoi] = useState<POI | null>(null);
  const [showRings, setShowRings] = useState(false);
  const [mobileTab, setMobileTab] = useState<'map' | 'filters' | 'stories'>('map');
  const [expandedTheme, setExpandedTheme] = useState<DemandTheme['id'] | null>(null);

  const mapRef = useRef<L.Map | null>(null);

  // Reset state on (re-)open so the modal is predictable each time
  useEffect(() => {
    if (!open) return;
    setActiveCats(new Set(CATEGORY_KEYS));
    setActiveThemeId(null);
    setSelectedPoi(null);
    setShowRings(false);
    setMobileTab('map');
    setExpandedTheme(null);
  }, [open]);

  // Body scroll lock + Esc to close
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  // ─── Filtering logic ─────────────────────────────────────────
  const visiblePois = useMemo(() => {
    return poisWithTheme.filter((p) => {
      if (p.category === 'project') return true; // KEY2 always visible
      if (!activeCats.has(p.category as CategoryKey)) return false;
      if (activeThemeId && p.themeId !== activeThemeId) return false;
      return true;
    });
  }, [poisWithTheme, activeCats, activeThemeId]);

  const toggleCategory = (k: CategoryKey) => {
    setActiveCats((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const toggleAll = () => {
    setActiveCats((prev) =>
      prev.size === CATEGORY_KEYS.length ? new Set() : new Set(CATEGORY_KEYS)
    );
  };

  const focusTheme = (theme: DemandTheme) => {
    setActiveThemeId((cur) => (cur === theme.id ? null : theme.id));
    setExpandedTheme(theme.id);
    setMobileTab('map');
    // Recentre on the theme's POIs
    if (mapRef.current && theme.poiNamesEn.length > 0) {
      const points = poisWithTheme
        .filter((p) => theme.poiNamesEn.includes(p.name_en))
        .map((p) => [p.lat, p.lng] as [number, number]);
      if (key2Poi) points.push([key2Poi.lat, key2Poi.lng]);
      if (points.length > 0) {
        const bounds = L.latLngBounds(points);
        mapRef.current.fitBounds(bounds, { padding: [60, 60], maxZoom: 14 });
      }
    }
  };

  // Distance helpers
  const distanceFromKey2 = (poi: POI) => {
    if (!key2Poi) return { km: 0, min: 0 };
    const km = haversineKm([key2Poi.lat, key2Poi.lng], [poi.lat, poi.lng]);
    return { km, min: poi.distance_min ?? approxDriveMin(km) };
  };

  if (!open) return null;

  // ─── Render ───────────────────────────────────────────────────
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('mapExperience.title')}
      dir={dir}
      className="fixed inset-0 z-[9999] bg-navy-900/95 flex flex-col text-white"
    >
      {/* ── Top bar ──────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 bg-navy-900/85 backdrop-blur border-b border-white/10">
        <div className="min-w-0 flex items-center gap-2">
          <Compass className="w-5 h-5 text-gold shrink-0" />
          <h2 className="text-base md:text-lg font-bold truncate">
            {t('mapExperience.title')}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <RingsToggle on={showRings} onChange={setShowRings} t={t} />
          <button
            type="button"
            onClick={onClose}
            aria-label={t('showcase.close')}
            title={t('showcase.close')}
            className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-red-500/70 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* ── Mobile tab strip ─────────────────────────────────── */}
      <div className="flex md:hidden border-b border-white/10 bg-navy-900/70">
        <MobileTab
          icon={MapIcon}
          label={t('mapExperience.tabMap')}
          active={mobileTab === 'map'}
          onClick={() => setMobileTab('map')}
        />
        <MobileTab
          icon={Filter}
          label={t('mapExperience.tabFilters')}
          active={mobileTab === 'filters'}
          onClick={() => setMobileTab('filters')}
        />
        <MobileTab
          icon={Sparkles}
          label={t('mapExperience.tabStories')}
          active={mobileTab === 'stories'}
          onClick={() => setMobileTab('stories')}
        />
      </div>

      {/* ── Body — split on desktop, swap on mobile ──────────── */}
      <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
        {/* Map column */}
        <div
          className={`relative flex-1 ${
            mobileTab === 'map' ? 'block' : 'hidden md:block'
          }`}
        >
          <MapContainer
            center={[center[0], center[1]]}
            zoom={13}
            scrollWheelZoom
            style={{ height: '100%', width: '100%' }}
            ref={(m) => {
              if (m) mapRef.current = m;
            }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              maxZoom={19}
            />

            {/* Drive-time rings */}
            {showRings && key2Poi && (
              <>
                <Circle
                  center={[key2Poi.lat, key2Poi.lng]}
                  radius={3000}
                  pathOptions={{
                    color: '#d4af37',
                    fillOpacity: 0.04,
                    weight: 1,
                    dashArray: '4 6',
                  }}
                />
                <Circle
                  center={[key2Poi.lat, key2Poi.lng]}
                  radius={6000}
                  pathOptions={{
                    color: '#d4af37',
                    fillOpacity: 0.03,
                    weight: 1,
                    dashArray: '4 6',
                  }}
                />
                <Circle
                  center={[key2Poi.lat, key2Poi.lng]}
                  radius={10000}
                  pathOptions={{
                    color: '#d4af37',
                    fillOpacity: 0.02,
                    weight: 1,
                    dashArray: '4 6',
                  }}
                />
              </>
            )}

            {/* Markers */}
            {visiblePois.map((p) => {
              const isKey2 = p.category === 'project';
              const meta = isKey2
                ? null
                : CATEGORY_META[p.category as CategoryKey];
              const ring = !!(activeThemeId && p.themeId === activeThemeId);
              const icon = buildMarkerIcon({
                color: isKey2 ? '#d4af37' : meta?.color ?? '#475569',
                size: isKey2 ? 26 : ring ? 22 : 18,
                ring: isKey2 || ring,
                pulse: isKey2,
              });
              return (
                <Marker
                  key={p.name_en}
                  position={[p.lat, p.lng]}
                  icon={icon}
                  eventHandlers={{
                    click: () => setSelectedPoi(p),
                  }}
                >
                  <Popup closeButton={false}>
                    <div dir="rtl" className="text-right text-sm">
                      <div className="font-semibold text-navy">{p.name_ar}</div>
                      {p.distance_min != null && (
                        <div className="text-xs text-navy-600 mt-0.5">
                          <span className="num-en">{p.distance_min}</span>{' '}
                          {t('mapExperience.minutes')}
                        </div>
                      )}
                    </div>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </div>

        {/* Sidebar column */}
        <aside
          className={`md:w-[380px] md:max-w-[40vw] md:border-s md:border-white/10 bg-navy-900/85 overflow-y-auto ${
            mobileTab === 'map' ? 'hidden md:block' : 'flex-1 block'
          }`}
        >
          {/* Filters block */}
          <div
            className={mobileTab === 'filters' ? 'block' : 'hidden md:block'}
          >
            <SidebarSection
              icon={Filter}
              title={t('mapExperience.tabFilters')}
            >
              <FilterChips
                active={activeCats}
                onToggle={toggleCategory}
                onToggleAll={toggleAll}
                t={t}
              />
            </SidebarSection>
          </div>

          {/* POI detail (always shown when a marker is selected) */}
          {selectedPoi && (
            <SidebarSection
              icon={MapIcon}
              title={isAr ? selectedPoi.name_ar : selectedPoi.name_en}
            >
              <PoiDetail
                poi={selectedPoi}
                isAr={isAr}
                themes={themes}
                distance={distanceFromKey2(selectedPoi)}
                t={t}
              />
            </SidebarSection>
          )}

          {/* Stories / Demand themes */}
          <div
            className={mobileTab === 'stories' ? 'block' : 'hidden md:block'}
          >
            <SidebarSection
              icon={Sparkles}
              title={t('mapExperience.demandThemes')}
            >
              <div className="space-y-2">
                {themes.map((th) => (
                  <ThemeCard
                    key={th.id}
                    theme={th}
                    isAr={isAr}
                    expanded={expandedTheme === th.id}
                    activeOnMap={activeThemeId === th.id}
                    onExpand={() =>
                      setExpandedTheme((cur) => (cur === th.id ? null : th.id))
                    }
                    onShowOnMap={() => focusTheme(th)}
                    t={t}
                  />
                ))}
              </div>
            </SidebarSection>
          </div>
        </aside>
      </div>

      {/* ── Bottom insight bar ───────────────────────────────── */}
      <div className="bg-gradient-to-r from-navy-900 via-navy-800 to-navy-900 border-t-2 border-gold/40 px-4 py-3 text-xs md:text-sm text-white/85 leading-relaxed">
        <div className="max-w-6xl mx-auto flex items-start gap-3">
          <Sparkles className="w-4 h-4 text-gold shrink-0 mt-0.5" />
          <p>{t('mapExperience.insightStatement')}</p>
        </div>
      </div>

      {/* tiny CSS for the gold pulsing pin */}
      <style>{`
        .me-pulse > span {
          animation: me-pulse 2s ease-in-out infinite;
        }
        @keyframes me-pulse {
          0%   { box-shadow: 0 0 0 0   rgba(212,175,55,.55), 0 1px 6px rgba(0,0,0,.4); }
          70%  { box-shadow: 0 0 0 14px rgba(212,175,55,0),    0 1px 6px rgba(0,0,0,.4); }
          100% { box-shadow: 0 0 0 0   rgba(212,175,55,0),     0 1px 6px rgba(0,0,0,.4); }
        }
        .leaflet-popup-content { margin: 8px 12px; }
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Sidebar section
// ─────────────────────────────────────────────────────────────
function SidebarSection({
  icon: Icon,
  title,
  children,
}: {
  icon: LucideIcon;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="px-4 py-4 border-b border-white/10">
      <h3 className="flex items-center gap-2 text-xs uppercase tracking-wider text-gold-300/80 mb-3">
        <Icon className="w-3.5 h-3.5" />
        {title}
      </h3>
      {children}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// Mobile tab
// ─────────────────────────────────────────────────────────────
function MobileTab({
  icon: Icon,
  label,
  active,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2.5 text-xs font-semibold border-b-2 transition-colors ${
        active
          ? 'border-gold text-gold'
          : 'border-transparent text-white/65 hover:text-white'
      }`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// Drive-time rings toggle
// ─────────────────────────────────────────────────────────────
function RingsToggle({
  on,
  onChange,
  t,
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  t: (k: StringKey) => string;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!on)}
      title={t('mapExperience.driveCircles')}
      aria-pressed={on}
      className={`hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-semibold transition-colors ${
        on
          ? 'bg-gold text-navy'
          : 'bg-white/10 text-white/85 hover:bg-white/20'
      }`}
    >
      <Compass className="w-3.5 h-3.5" />
      <span className="num-en">5/10/15</span>
      <span>{t('mapExperience.minutes')}</span>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// Filter chips
// ─────────────────────────────────────────────────────────────
function FilterChips({
  active,
  onToggle,
  onToggleAll,
  t,
}: {
  active: Set<CategoryKey>;
  onToggle: (k: CategoryKey) => void;
  onToggleAll: () => void;
  t: (k: StringKey) => string;
}) {
  const allOn = active.size === CATEGORY_KEYS.length;
  return (
    <div className="flex flex-wrap gap-1.5">
      <button
        type="button"
        onClick={onToggleAll}
        aria-pressed={allOn}
        className={`inline-flex items-center gap-1 h-8 px-3 rounded-full text-xs font-semibold transition-colors ${
          allOn
            ? 'bg-gold text-navy'
            : 'bg-white/10 text-white/85 hover:bg-white/20'
        }`}
      >
        {t('mapExperience.allCategories')}
      </button>
      {CATEGORY_KEYS.map((k) => {
        const meta = CATEGORY_META[k];
        const Icon = meta.icon;
        const on = active.has(k);
        return (
          <button
            key={k}
            type="button"
            onClick={() => onToggle(k)}
            aria-pressed={on}
            className={`inline-flex items-center gap-1.5 h-8 px-2.5 rounded-full text-xs font-medium transition-colors border ${
              on
                ? `${meta.bgClass} text-white border-transparent`
                : 'bg-white/5 text-white/70 border-white/15 hover:bg-white/10'
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {t(`mapCat.${k}` as StringKey)}
          </button>
        );
      })}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// POI detail card
// ─────────────────────────────────────────────────────────────
function PoiDetail({
  poi,
  isAr,
  themes,
  distance,
  t,
}: {
  poi: POI;
  isAr: boolean;
  themes: DemandTheme[];
  distance: { km: number; min: number };
  t: (k: StringKey) => string;
}) {
  const cat = poi.category as CategoryKey;
  const meta = CATEGORY_META[cat];
  const theme = themes.find((th) => th.poiNamesEn.includes(poi.name_en));
  const themeIcon = theme ? THEME_ICON[theme.id] : null;
  const gmapsHref = `https://www.google.com/maps/dir/?api=1&destination=${poi.lat},${poi.lng}`;

  return (
    <div className="space-y-3">
      <div className="text-sm text-white/85">
        {isAr ? poi.name_en : poi.name_ar}
      </div>

      <div className="flex flex-wrap gap-1.5">
        {meta && (
          <span
            className={`inline-flex items-center gap-1 ${meta.bgClass} text-white text-[11px] font-semibold px-2 py-0.5 rounded-full`}
          >
            <meta.icon className="w-3 h-3" />
            {t(`mapCat.${cat}` as StringKey)}
          </span>
        )}
        {theme && themeIcon && (
          <span
            className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full text-white"
            style={{ backgroundColor: theme.color }}
          >
            {(() => {
              const I = themeIcon;
              return <I className="w-3 h-3" />;
            })()}
            {isAr ? theme.titleAr : theme.titleEn}
          </span>
        )}
      </div>

      <div className="text-xs text-white/75">
        <div>{t('mapExperience.distFromKey2')}:</div>
        <div className="mt-0.5 num-en text-white">
          {distance.km.toFixed(1)} {t('mapExperience.km')} · {distance.min}{' '}
          {t('mapExperience.minutes')}
        </div>
      </div>

      {poi.demand && (
        <div className="text-xs text-white/70 leading-relaxed border-s-2 border-gold/40 ps-2.5">
          {poi.demand}
        </div>
      )}

      <a
        href={gmapsHref}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 mt-1 px-3 py-1.5 rounded-full text-xs font-semibold bg-gold text-navy hover:bg-gold-300 transition-colors"
      >
        <ExternalLink className="w-3.5 h-3.5" />
        {t('mapExperience.openInMaps')}
      </a>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Theme card
// ─────────────────────────────────────────────────────────────
function ThemeCard({
  theme,
  isAr,
  expanded,
  activeOnMap,
  onExpand,
  onShowOnMap,
  t,
}: {
  theme: DemandTheme;
  isAr: boolean;
  expanded: boolean;
  activeOnMap: boolean;
  onExpand: () => void;
  onShowOnMap: () => void;
  t: (k: StringKey) => string;
}) {
  const Icon = THEME_ICON[theme.id];
  const title = isAr ? theme.titleAr : theme.titleEn;
  const desc = isAr ? theme.descAr : theme.descEn;

  return (
    <div
      className={`rounded-xl border transition-colors ${
        activeOnMap
          ? 'border-gold bg-gold/5'
          : 'border-white/10 bg-white/[0.03]'
      }`}
    >
      <button
        type="button"
        onClick={onExpand}
        className="w-full flex items-center gap-3 px-3 py-2.5 text-start"
      >
        <span
          className="inline-flex items-center justify-center w-8 h-8 rounded-full text-white"
          style={{ backgroundColor: theme.color }}
        >
          <Icon className="w-4 h-4" />
        </span>
        <span className="flex-1 font-semibold text-sm">{title}</span>
        {expanded ? (
          <ChevronUp className="w-4 h-4 text-white/65" />
        ) : (
          <ChevronDown className="w-4 h-4 text-white/65" />
        )}
      </button>

      {expanded && (
        <div className="px-3 pb-3 pt-1 space-y-2">
          <p className="text-xs text-white/80 leading-relaxed">{desc}</p>
          <button
            type="button"
            onClick={onShowOnMap}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              activeOnMap
                ? 'bg-gold text-navy'
                : 'bg-white/10 text-white hover:bg-white/20'
            }`}
          >
            {t('mapExperience.showOnMap')}
          </button>
        </div>
      )}
    </div>
  );
}
